import { useEffect, useRef, useState } from "react";
import { useReducedMotion } from "framer-motion";
import { SITE } from "@/lib/config";
import { cn, hexId } from "@/lib/utils";
import { MonoLabel, StatusDot } from "@/components/primitives";

type Kind = "in" | "out" | "ok" | "err" | "sys";
interface TermLine {
  id: number;
  kind: Kind;
  text: string;
}

const BOOT: { text: string; kind: Kind }[] = [
  { text: "CONSEQUENCE TERMINAL v1.0 — simulated session", kind: "sys" },
  { text: "All responses are generated locally. No blockchain queries are made.", kind: "sys" },
  { text: "Type 'help' for available commands.", kind: "sys" },
];

const RESPONSES: Record<string, { desc: string; out: () => { text: string; kind: Kind }[] }> = {
  help: {
    desc: "list available commands",
    out: () => [
      { text: "AVAILABLE COMMANDS:", kind: "out" },
      ...Object.keys(RESPONSES).map((c) => ({ text: `  ${c.padEnd(12)} ${RESPONSES[c].desc}`, kind: "out" as Kind })),
      { text: "  clear        clear the terminal", kind: "out" },
    ],
  },
  status: {
    desc: "protocol subsystem status",
    out: () => [
      { text: "> loading outcome registry...", kind: "out" },
      { text: "[OK] outcome registry — 9 entries (DEMO)", kind: "ok" },
      { text: "> checking verification layer...", kind: "out" },
      { text: "[OK] verification layer — deterministic mode", kind: "ok" },
      { text: "> syncing consequence graph...", kind: "out" },
      { text: "[OK] consequence graph — synced", kind: "ok" },
      { text: "> reputation engine...", kind: "out" },
      { text: "[OK] reputation engine — nominal", kind: "ok" },
      { text: "> autonomous economy...", kind: "out" },
      { text: "[ONLINE] autonomous economy — concept mode", kind: "ok" },
    ],
  },
  outcomes: {
    desc: "recent registry entries (demo)",
    out: () => [
      { text: "ID        ACTOR         OBJECTIVE              RESULT   STATUS", kind: "out" },
      { text: "OC-0471   AGENT #0471   REVENUE GROWTH         +27.4%   VERIFIED", kind: "ok" },
      { text: "OC-0922   DEV #0119     GAS REDUCTION          -37%     VERIFIED", kind: "ok" },
      { text: "OC-0999   DEV #0233     BRIDGE LATENCY         PENDING  ACTIVE", kind: "out" },
      { text: "OC-0555   AGENT #201    LIQUIDITY EXPANSION    +9.8%    FAILED", kind: "err" },
      { text: "SIMULATED REGISTRY — demonstration data only", kind: "sys" },
    ],
  },
  reputation: {
    desc: "example consequence profile (demo)",
    out: () => [
      { text: "AGENT #0471 — CONSEQUENCE PROFILE", kind: "out" },
      { text: "  score            94.7 / 100", kind: "ok" },
      { text: "  verified         1,691", kind: "out" },
      { text: "  success rate     91.8%", kind: "out" },
      { text: "  economic impact  $18.2M", kind: "out" },
      { text: "  disputes         7 (7 resolved)", kind: "out" },
      { text: "SIMULATED PROFILE — not a live protocol metric", kind: "sys" },
    ],
  },
  network: {
    desc: "network information (simulated)",
    out: () => [
      { text: `network      ${SITE.network}`, kind: "out" },
      { text: "rpc          simulated — no live connection", kind: "out" },
      { text: "latency      41ms (fabricated)", kind: "out" },
      { text: `ticker       ${SITE.ticker}`, kind: "out" },
      { text: `contract     ${SITE.contractAddress}`, kind: "ok" },
    ],
  },
  protocol: {
    desc: "the protocol pipeline",
    out: () => [
      { text: "ACTION → CLAIM → EXECUTE → OBSERVE → VERIFY → CONSEQUENCE → REPUTATION", kind: "ok" },
      { text: "Proof of Consequence: the measure is not whether you acted —", kind: "out" },
      { text: "it is what happened because you acted.", kind: "out" },
    ],
  },
  about: {
    desc: "what this protocol is",
    out: () => [
      { text: "Consequence Protocol is an outcome verification layer.", kind: "out" },
      { text: `It measures what actions actually achieve — transforming verified consequences into reputation, trust, and economic access. ${SITE.tagline}`, kind: "out" },
      { text: "This terminal is a concept interface. Nothing is deployed.", kind: "sys" },
    ],
  },
  social: {
    desc: "official channels",
    out: () => [{ text: `x — ${SITE.xUrl}`, kind: "out" }],
  },
};

const SUGGESTIONS = ["help", "status", "outcomes", "reputation", "network", "protocol", "clear"];

export function TerminalConsole() {
  const idRef = useRef(0);
  const reduced = useReducedMotion();
  const [lines, setLines] = useState<TermLine[]>(() =>
    BOOT.map((l) => ({ id: idRef.current++, ...l }))
  );
  const [input, setInput] = useState("");
  const [history, setHistory] = useState<string[]>([]);
  const [histIdx, setHistIdx] = useState(-1);
  const bodyRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const queue = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    bodyRef.current?.scrollTo({ top: bodyRef.current.scrollHeight });
  }, [lines]);

  useEffect(() => () => queue.current.forEach(clearTimeout), []);

  const push = (items: { text: string; kind: Kind }[]) => {
    if (reduced) {
      setLines((prev) => [...prev, ...items.map((l) => ({ id: idRef.current++, ...l }))]);
      return;
    }
    items.forEach((l, i) => {
      const t = setTimeout(() => {
        setLines((prev) => [...prev, { id: idRef.current++, ...l }]);
      }, i * 70);
      queue.current.push(t);
    });
  };

  const run = (raw: string) => {
    const cmd = raw.trim().toLowerCase();
    if (!cmd) return;
    setLines((prev) => [...prev, { id: idRef.current++, kind: "in", text: raw.trim() }]);
    setHistory((h) => [raw.trim(), ...h]);
    setHistIdx(-1);

    if (cmd === "clear") {
      setLines([]);
      return;
    }
    const def = RESPONSES[cmd];
    if (def) {
      push(def.out());
    } else {
      push([
        { text: `command not found: ${cmd}`, kind: "err" },
        { text: "type 'help' for available commands.", kind: "sys" },
      ]);
    }
  };

  const onKeyDown = (e: import("react").KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      run(input);
      setInput("");
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      const next = Math.min(histIdx + 1, history.length - 1);
      if (history[next] !== undefined) {
        setHistIdx(next);
        setInput(history[next]);
      }
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      const next = histIdx - 1;
      setHistIdx(next);
      setInput(next >= 0 ? history[next] : "");
    } else if (e.key === "Tab") {
      e.preventDefault();
      const match = Object.keys(RESPONSES).find((c) => c.startsWith(input.trim().toLowerCase()));
      if (match) setInput(match);
    }
  };

  const lineColor: Record<Kind, string> = {
    in: "text-ink",
    out: "text-dim",
    ok: "text-success",
    err: "text-error",
    sys: "text-cyan/80",
  };

  return (
    <div className="flex h-full flex-col" data-testid="terminal-console">
      <div
        ref={bodyRef}
        data-testid="terminal-output-feed"
        onClick={() => inputRef.current?.focus()}
        className="scanlines relative flex-1 overflow-y-auto p-4 font-mono text-xs leading-relaxed sm:text-[13px]"
        aria-live="polite"
      >
        {lines.map((l) => (
          <p key={l.id} className={cn("py-0.5 break-words", lineColor[l.kind])}>
            {l.kind === "in" ? <span className="text-cyan">visitor@consequence:~$ </span> : null}
            {l.text}
          </p>
        ))}
      </div>

      <div className="border-t border-line px-4 py-3">
        <div className="mb-3 flex flex-wrap gap-2" data-testid="terminal-suggestions">
          {SUGGESTIONS.map((c) => (
            <button
              key={c}
              data-testid={`terminal-cmd-chip-${c}`}
              onClick={() => run(c)}
              className="border border-line px-2.5 py-1 font-mono text-[10px] lowercase tracking-[0.12em] text-dim transition-colors duration-150 hover:border-cyan/50 hover:text-cyan"
            >
              {c}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <span className="shrink-0 font-mono text-xs text-cyan sm:text-[13px]">visitor@consequence:~$</span>
          <input
            ref={inputRef}
            data-testid="terminal-input-field"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            className="w-full bg-transparent font-mono text-xs text-ink caret-[#00E5FF] outline-none sm:text-[13px]"
            autoComplete="off"
            autoCapitalize="off"
            spellCheck={false}
            aria-label="Terminal command input"
          />
          <span className="hidden font-mono text-[9px] text-dim/50 sm:block">{hexId(8)}</span>
        </div>
      </div>
    </div>
  );
}
